import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useInvalidateQueryOnUnmount from 'hooks/useInvalidateQueryOnUnmount';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import {
  DataRoomDashboardTimeframe,
  HotLeadAggregationResult,
} from 'router/subrouters/DataRoom/models/Dashboard';
import { PaginatedData } from 'models/PaginatedData';
import {
  getInfiniteQueryItems,
  getInfiniteQueryTotalItems,
  getNextPageParam,
} from 'queryUtils';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';

const HOT_LEADS_QUERY_KEY = 'data-room-hot-leads';
const LIMIT = '10';

type UseHotLeadsInfiniteQueryProps = {
  dataRoomId?: string;
  timeframe?: DataRoomDashboardTimeframe;
  sort?: string;
};

export default function useHotLeadsInfiniteQuery({
  dataRoomId,
  timeframe,
  sort,
}: UseHotLeadsInfiniteQueryProps) {
  const getMixpanelProps = useGetMixpanelProps();

  const query = useInfiniteQuery<PaginatedData<HotLeadAggregationResult>>({
    queryKey: [HOT_LEADS_QUERY_KEY, { dataRoomId, timeframe, sort }],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await dataRoomApi.room.getHotLeads(dataRoomId, {
        $page: pageParam.toString(),
        $limit: LIMIT,
        timeframe,
        $sort: sort,
      });

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: !!dataRoomId && !!timeframe,
    keepPreviousData: true,
    getNextPageParam,
  });

  const items = useMemo(() => getInfiniteQueryItems(query.data), [query.data]);

  const totalItems = getInfiniteQueryTotalItems(query.data);

  useInvalidateQueryOnUnmount(HOT_LEADS_QUERY_KEY);

  return { query, items, totalItems };
}
